import {
  CopyValue,
  IncentiveDetailsError,
  IncentiveDetailsInput,
  IncentiveDetailsSuccess,
  Query,
  VxlIconKey,
} from '@varo/graphql-schema-types';
import {
  getIntervalTrackerStyle,
  getTagType,
  StepState,
  TrackerState,
} from 'features/rewards/Details/MultiStepIncentiveScreen/multiStepProgress.helper';
import FetchIncentiveDetails from 'services/graphql/queries/FetchIncentiveDetails';
import { MarkProps } from 'shared/components/vxl/ProgressIndicator/IntervalTracker/base/Marker/Marker';
import { StepProps } from 'shared/components/vxl/ProgressIndicator/StepChecklist/base/Step/Step';
import theme from 'shared/styles/theme';
import { capitalizeWord } from 'shared/utils/string/string.helpers';
import { useQuery } from 'urql';

const useIncentiveDetails = (offerId: string) => {
  const input: IncentiveDetailsInput = { offerId };
  const [{ data, fetching: loading, error }] = useQuery<Query>({
    query: FetchIncentiveDetails,
    variables: { input },
    requestPolicy: 'cache-and-network',
  });

  const incentiveDetails = data?.incentiveDetails as IncentiveDetailsSuccess;
  const errorData = data?.incentiveDetails as IncentiveDetailsError;

  const trackerStates = incentiveDetails?.intervals?.map((interval, index) => {
    const formattedState = interval?.state
      ? capitalizeWord(interval.state)
      : '';

    const { borderColor, color, tintColor } = getIntervalTrackerStyle(
      formattedState as TrackerState,
    );

    return {
      id: String(index),
      status: formattedState,
      borderColor,
      content: (interval?.asset as CopyValue)?.text,
      caption: {
        label: interval?.caption,
        style: { color },
      },
      iconKey: '',
      tintColor,
    } as MarkProps;
  });

  const intervalTrackerData = {
    trackerStates,
    trackerHeading: incentiveDetails?.intervalsHeading?.text,
  };

  const stepperStates = incentiveDetails?.steps?.map((step, index) => ({
    id: index.toString(),
    stepState: step?.state?.toLowerCase() as StepState,
    indicator: {
      icon: step.icon as VxlIconKey,
      tintColor: theme.color.interactive.primary,
    },
    content: {
      descriptionText: step?.descriptionText,
      annotation: step?.annotationText as string | React.ReactNode,
    },
  }));

  const tagProps = {
    text: incentiveDetails?.stepsHeading?.tag?.text,
    type: getTagType(incentiveDetails?.stepsHeading?.tag?.tagColor),
    iconKey: incentiveDetails?.stepsHeading?.tag?.icon,
  };

  const stepCheckListData = {
    stepperHeading: incentiveDetails?.stepsHeading?.text,
    tagContent: tagProps,
    stepperStates: stepperStates as StepProps[],
  };

  return {
    incentiveDetails,
    loading,
    error: errorData?.code || error,
    intervalTrackerData,
    stepCheckListData,
  };
};

export default useIncentiveDetails;
